import { useEffect, useMemo, useState } from "react";
import { Input, Modal } from "antd";
import { CloseOutlined, PlusOutlined, SearchOutlined, SwapOutlined } from "@ant-design/icons";
import type { AdminTag } from "../../../../../queries/casino-configuration/games/fetchAdminTags";
import { AppStrings } from "../../../../../utils/constants/AppStrings";
import { formatString } from "../../../../../utils/constants/stringUtils";
import { useCurationDisplayName } from "../../../../../hooks/useCurationDisplayName";

const TAG_COLORS: Record<string, string> = {
  exclusive: "#9b59b6",
  hot: "#e74c3c",
  live: "#2ecc71",
  "members only": "#1abc9c",
  new: "#3498db",
  premium: "#f39c12",
};

function getTagColor(tag: AdminTag): string {
  if (tag.color) return tag.color;
  return TAG_COLORS[tag.name.toLowerCase()] ?? "#9ea2b8";
}

function isTagCurrent(
  tagOrId: AdminTag | string,
  attachedIds: readonly string[],
): boolean {
  return attachedIds.some((attachedId) => {
    const id = String(attachedId);
    if (typeof tagOrId === "string") {
      return tagOrId === id || tagOrId.toLowerCase() === id.toLowerCase();
    }
    return tagOrId.id === id || tagOrId.name.toLowerCase() === id.toLowerCase();
  });
}

type TagModalProps = {
  open: boolean;
  gameLabel: string;
  currentTags: string[];
  availableTags: AdminTag[];
  loading: boolean;
  onSetTag: (tagIds: string[]) => void | Promise<void>;
  onRemoveTag?: (tagId: string) => void | Promise<void>;
  onClose: () => void;
};

function TagModal({
  open,
  gameLabel,
  currentTags,
  availableTags,
  loading,
  onSetTag,
  onRemoveTag,
  onClose,
}: TagModalProps) {
  const { getName: getTagDisplayName } = useCurationDisplayName();
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<string | null>(null);
  const [localTags, setLocalTags] = useState<string[]>(currentTags);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (open && !dirty) {
      setLocalTags(currentTags);
      setSelected(null);
    }
  }, [open, currentTags, dirty]);

  const effectiveTags = open ? localTags : currentTags;
  const attachedIds = effectiveTags.map(String);
  const lastAttachedTagId =
    attachedIds.length > 0 ? attachedIds[attachedIds.length - 1] : null;
  const hasCurrentTag = attachedIds.length > 0;
  const selectedIsCurrent =
    selected != null &&
    (isTagCurrent(selected, attachedIds) ||
      availableTags.some((t) => t.id === selected && isTagCurrent(t, attachedIds)));
  const isReplacing = hasCurrentTag && selected != null && !selectedIsCurrent;

  const filteredTags = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return availableTags;
    return availableTags.filter((t) => t.name.toLowerCase().includes(q));
  }, [availableTags, search]);

  const handleSet = async () => {
    if (!selected || selectedIsCurrent) return;
    const prevTags = localTags;
    setBusy(true);
    setDirty(true);
    try {
      if (isReplacing && onRemoveTag) {
        for (const tagId of attachedIds) {
          await onRemoveTag(tagId);
        }
      }
      await onSetTag([selected]);
      setLocalTags([selected]);
      setSelected(null);
    } catch {
      setLocalTags(prevTags);
      setDirty(false);
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async (tagId: string) => {
    if (!onRemoveTag) return;
    setBusy(true);
    setDirty(true);
    try {
      await onRemoveTag(tagId);
      setLocalTags((prev) => prev.filter((id) => id !== tagId));
    } catch {
      setDirty(false);
    } finally {
      setBusy(false);
    }
  };

  const handleAfterClose = () => {
    setSearch("");
    setSelected(null);
    setLocalTags(currentTags);
    setDirty(false);
  };

  const lastAttachedTag = lastAttachedTagId
    ? availableTags.find((t) => isTagCurrent(t, [lastAttachedTagId]))
    : null;

  return (
    <Modal
      open={open}
      rootClassName="casino-games-modal-root"
      className="casino-games-tag-modal"
      title={
        <span className="casino-games-modal-title">
          {AppStrings.CASINO_CONFIG_GAMES_TAG_MODAL_TITLE.replace(
            "{id}",
            gameLabel,
          )}
        </span>
      }
      closeIcon={<CloseOutlined />}
      onCancel={onClose}
      afterClose={handleAfterClose}
      footer={null}
      width={520}
    >
      <div className="casino-games-tag-modal-desc">
        {AppStrings.CASINO_CONFIG_GAMES_TAG_MODAL_DESC}
      </div>

      <div className="casino-games-tag-attached-section">
        <div className="casino-games-tag-attached-label">
          {effectiveTags.length > 1
            ? `${AppStrings.CASINO_CONFIG_GAMES_TAG_ATTACHED} ${AppStrings.CASINO_CONFIG_GAMES_TAG_MULTIPLE_LABEL}`
            : AppStrings.CASINO_CONFIG_GAMES_TAG_ATTACHED}
        </div>
        {effectiveTags.length > 0 ? (
          <div className="casino-games-tag-attached-badges">
            {effectiveTags.map((tagId) => {
              const tagObj = availableTags.find((t) => isTagCurrent(t, [String(tagId)]));
              const name = tagObj ? getTagDisplayName(tagObj) : String(tagId);
              const color = tagObj ? getTagColor(tagObj) : "#9ea2b8";
              return (
                <span
                  key={tagId}
                  className="casino-games-tag-badge casino-games-tag-badge--attached"
                  style={{ borderColor: color, color }}
                  title={tagObj && name !== tagObj.name ? tagObj.name : undefined}
                >
                  {name}
                  {onRemoveTag && (
                    <button
                      type="button"
                      className="casino-games-tag-badge-remove"
                      onClick={() => handleRemove(tagId)}
                      disabled={busy}
                      aria-label={formatString(
                        AppStrings.CASINO_CONFIG_COLLECTIONS_REMOVE_ROW_ARIA,
                        { name },
                      )}
                    >
                      ×
                    </button>
                  )}
                </span>
              );
            })}
            {effectiveTags.length > 1 && (
              <span className="casino-games-tag-legacy-warning">
                {AppStrings.CASINO_CONFIG_GAMES_TAG_LEGACY_WARNING}
              </span>
            )}
          </div>
        ) : (
          <span className="casino-games-tag-attached-none">
            {AppStrings.CASINO_CONFIG_GAMES_TAG_NONE}
          </span>
        )}
      </div>

      <Input
        className="casino-games-tag-search"
        prefix={<SearchOutlined />}
        placeholder={AppStrings.CASINO_CONFIG_GAMES_TAG_SEARCH}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        allowClear
      />

      <div className="casino-games-tag-list">
        {filteredTags.map((tag) => {
          const tagColor = getTagColor(tag);
          const isCurrentTag = isTagCurrent(tag, attachedIds);
          const isSelected = selected === tag.id;
          return (
            <label
              key={tag.id}
              className={`casino-games-tag-option${isSelected ? " casino-games-tag-option--selected" : ""}${isCurrentTag ? " casino-games-tag-option--current" : ""}`}
            >
              <input
                type="radio"
                name="casino-tag-select"
                className="casino-games-tag-radio"
                checked={isSelected}
                disabled={isCurrentTag || busy}
                onChange={() => setSelected(tag.id)}
              />
              <span
                className="casino-games-tag-badge"
                style={{ borderColor: tagColor, color: tagColor }}
                title={
                  getTagDisplayName(tag) !== tag.name ? tag.name : undefined
                }
              >
                {getTagDisplayName(tag)}
              </span>
              {tag.seeded ? (
                <span className="casino-games-tag-seeded">
                  {AppStrings.CASINO_CONFIG_GAMES_TAG_SEEDED}
                </span>
              ) : null}
              {isCurrentTag ? (
                <span className="casino-games-tag-current">
                  {AppStrings.CASINO_CONFIG_GAMES_TAG_CURRENT}
                </span>
              ) : null}
              {isSelected && hasCurrentTag && !isCurrentTag && lastAttachedTag ? (
                <span className="casino-games-tag-replaces">
                  {AppStrings.CASINO_CONFIG_GAMES_TAG_REPLACES.replace(
                    "{name}",
                    getTagDisplayName(lastAttachedTag),
                  )}
                </span>
              ) : null}
            </label>
          );
        })}
      </div>

      <div className="casino-games-tag-modal-footer">
        <button
          type="button"
          className="casino-games-tag-close-btn"
          onClick={onClose}
        >
          {AppStrings.CASINO_CONFIG_GAMES_TAG_CLOSE}
        </button>
        <button
          type="button"
          className="casino-games-tag-set-btn"
          disabled={!selected || selectedIsCurrent || loading || busy}
          onClick={handleSet}
        >
          {isReplacing ? (
            <>
              <SwapOutlined />
              {AppStrings.CASINO_CONFIG_GAMES_TAG_REPLACE}
            </>
          ) : (
            <>
              <PlusOutlined />
              {AppStrings.CASINO_CONFIG_GAMES_TAG_SET}
            </>
          )}
        </button>
      </div>
    </Modal>
  );
}

export default TagModal;
